import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import activityService from '../services/activityService';
import { ActivityFeedItem } from '../types';
import { STATUS_CONFIG } from './Buildings';

const ActivityFeed: React.FC = () => {
  const [items, setItems] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    activityService.getActivity(50)
      .then(setItems)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight">Aktivitäten</h1>
        <p className="text-base text-slate-400 mt-1">Letzte Ereignisse im Portal</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#216dab]/30 border-t-[#216dab] rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-slate-400 font-medium">Noch keine Aktivitäten vorhanden</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={`${item.type}-${item.id}`} className="glass-card p-4 flex gap-4">
              {/* Icon */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${
                item.type === 'NOTE'
                  ? 'bg-[#C7E4FF] dark:bg-[#0d3d5f]/40 text-[#216dab] dark:text-[#3aa6e9]'
                  : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
              }`}>
                {item.type === 'NOTE' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
                  <Link
                    to={`/buildings/${item.buildingId}`}
                    className="font-semibold text-base text-slate-800 dark:text-white hover:text-[#216dab] dark:hover:text-[#3aa6e9] transition-colors"
                  >
                    {item.buildingName}
                  </Link>
                  <span className="text-sm text-slate-400">·</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{item.authorName}</span>
                  <span className="text-sm text-slate-400">·</span>
                  <span className="text-sm text-slate-400">{formatDate(item.createdAt)}</span>
                </div>

                {item.type === 'NOTE' && item.text && (
                  <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed">{item.text}</p>
                )}

                {item.type === 'STATUS_CHANGE' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.oldStatus ? (
                      <span className={`inline-flex px-2 py-0.5 rounded-md border text-sm font-medium ${STATUS_CONFIG[item.oldStatus]?.color ?? 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_CONFIG[item.oldStatus]?.label ?? item.oldStatus}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-400 italic">—</span>
                    )}
                    <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    {item.newStatus && (
                      <span className={`inline-flex px-2 py-0.5 rounded-md border text-sm font-medium ${STATUS_CONFIG[item.newStatus]?.color ?? 'bg-slate-100 text-slate-600'}`}>
                        {STATUS_CONFIG[item.newStatus]?.label ?? item.newStatus}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
